import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAdminAuth } from "@/lib/adminAuth";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";

function formatErr(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Login failed.";
}

export default function AdminLogin() {
  const { authed, checking, login } = useAdminAuth();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-ivory text-charcoal/60 text-xs uppercase tracking-[0.22em]">Checking session…</div>;
  }
  if (authed) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      await login(password);
      toast.success("Welcome back, Stefanie.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Wrong password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-6 py-16" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="overline justify-center inline-block">— Restricted</div>
          <h1 className="font-serif-heading text-4xl md:text-5xl text-charcoal mt-3">Admin Access</h1>
          <p className="text-ink text-sm mt-3">For Stefanie. Enter your admin password to review leads &amp; messages.</p>
        </div>
        <form onSubmit={onSubmit} className="border border-charcoal/10 bg-ivory p-8 md:p-10" data-testid="admin-login-form">
          <label htmlFor="pw" className="overline block mb-3">Password</label>
          <div className="relative">
            <input
              id="pw"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="line-input pl-7"
              placeholder="••••••••"
              data-testid="admin-login-password"
              required
            />
            <Lock size={14} className="absolute left-0 top-3 text-charcoal/40" strokeWidth={1.5} />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full mt-8 disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? (<><Loader2 size={14} className="animate-spin mr-2" /> Signing in…</>) : "Sign In"}
          </button>
        </form>
        <p className="text-center text-xs text-mute mt-6">
          <a href="/" className="hover:text-bronze underline-offset-2 hover:underline">← Back to site</a>
        </p>
      </div>
    </div>
  );
}
